import { useEffect, useState } from 'react';
import { PrimaryButton } from './PrimaryButton';
import {
  listCustomerSubscriptionPlans,
  type CustomerSubscriptionPlan,
} from '../../lib/booking-api';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

interface SubscriptionPlanOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onPickPlan?: (planId: string) => void;
  onSkip?: () => void;
  showArrow?: boolean;
}

function formatPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function SubscriptionPlanOffcanvas({
  isOpen,
  onClose,
  onPickPlan,
  onSkip,
  showArrow = false,
}: SubscriptionPlanOffcanvasProps) {
  const [plans, setPlans] = useState<CustomerSubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);

  // Load plans lazily on first open so the offcanvas doesn't fire a
  // public API call on every page that mounts <BookingFlowPage />.
  useEffect(() => {
    if (!isOpen) return;
    if (plans.length > 0) return;
    let cancelled = false;
    setLoading(true);
    listCustomerSubscriptionPlans()
      .then((res) => {
        if (cancelled) return;
        setPlans(
          (res.list ?? [])
            .filter((p) => p.is_active)
            .slice()
            .sort((a, b) => a.sort_order - b.sort_order),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(apiErrorMessage(err, "Couldn't load plans."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, plans.length]);

  return (
    <>
      {/* Backdrop — needs to sit above the sticky MainHeader (z-50) so
          clicks land on the offcanvas, not on the page underneath. */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[100] transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Offcanvas Panel (Left side) */}
      <div
        className={`fixed inset-y-0 left-0 w-full max-w-[500px] bg-white shadow-2xl z-[110] transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="h-full overflow-y-auto p-8 md:p-12">
          {/* Title + visible Cancel button. Previously the offcanvas
              had no in-panel close affordance — customers had to
              click the dimmed backdrop, which was easy to miss and
              left them feeling stuck. Cancel is always-on so the
              flow is reversible from inside the panel. */}
          <div className="flex items-center justify-between mb-8 gap-3">
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[26px] leading-[36px] tracking-[-0.0944px] whitespace-nowrap">
              Subscription Plans
            </h1>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cancel and close subscription plans"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-[8px] border border-[#ede8e9] bg-white hover:border-[#8e2a2f] hover:text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[13px] text-[#3a3335] transition-colors shrink-0"
            >
              <span className="material-icons text-[18px]">close</span>
              Cancel
            </button>
          </div>

          {loading ? (
            <p className="text-sm text-[#908e8e]">Loading plans…</p>
          ) : plans.length === 0 ? (
            <p className="text-sm text-[#908e8e]">
              No subscription plans available right now.
            </p>
          ) : (
            <div className="space-y-6">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="border border-[#eee] rounded-[12px] p-6 shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.05)]"
                >
                  {/* Card Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[22px] leading-[30px] mb-1">
                        {plan.name}
                      </h2>
                      {plan.tagline ? (
                        <p
                          className="font-['DM_Sans',sans-serif] text-[#908e8e] text-sm leading-[21px] tracking-[-0.1px]"
                          style={{ fontVariationSettings: "'opsz' 14" }}
                        >
                          {plan.tagline}
                        </p>
                      ) : null}
                    </div>
                    <PrimaryButton
                      className="!h-10 !px-4 !text-sm whitespace-nowrap min-w-max"
                      onClick={() => onPickPlan?.(plan.id)}
                    >
                      Pick Plan
                    </PrimaryButton>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-[#eee] w-full mb-6" />

                  {/* Price */}
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[28px] leading-[36px]">
                      {formatPrice(plan.monthly_price, plan.currency)}
                    </span>
                    <span
                      className="font-['DM_Sans',sans-serif] text-[#908e8e] text-sm leading-[21px] tracking-[-0.1px]"
                      style={{ fontVariationSettings: "'opsz' 14" }}
                    >
                      /month
                    </span>
                    {plan.yearly_price > 0 ? (
                      <span
                        className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] leading-[18px] ml-3"
                        style={{ fontVariationSettings: "'opsz' 12" }}
                      >
                        or {formatPrice(plan.yearly_price, plan.currency)}/yr
                      </span>
                    ) : null}
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-[#eee] w-full mb-6" />

                  {/* Features List */}
                  {plan.features && plan.features.length > 0 ? (
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3">
                          <span className="material-icons text-[#8e2a2f] text-[18px]">
                            check
                          </span>
                          <span
                            className="font-['DM_Sans',sans-serif] text-[#3a3335] text-sm leading-[21px] tracking-[-0.1px]"
                            style={{ fontVariationSettings: "'opsz' 14" }}
                          >
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {plan.footer_note ? (
                    <>
                      <div className="h-px bg-[#eee] w-full mb-6" />
                      <p
                        className="font-['DM_Sans',sans-serif] text-[#908e8e] text-xs leading-[18px] tracking-[-0.1px]"
                        style={{ fontVariationSettings: "'opsz' 14" }}
                      >
                        {plan.footer_note}
                      </p>
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          {/* Skip Button */}
          {onSkip && (
            <button
              onClick={onSkip}
              className="w-full mt-8 text-center font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-sm hover:text-[#8e2a2f] transition-colors cursor-pointer underline underline-offset-4"
            >
              Skip for now
            </button>
          )}
        </div>

        {/* Close Button (Arrow) */}
        {showArrow && (
          <button
            onClick={onClose}
            className="absolute -right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-[0px_4px_15.3px_0px_rgba(0,0,0,0.1)] flex items-center justify-center transition-all hover:scale-110 z-[60]"
          >
            <span className="material-icons text-[#3a3335] text-[24px]">
              navigate_next
            </span>
          </button>
        )}
      </div>
    </>
  );
}
